import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { QRCodeComponent } from 'angularx-qrcode';

export interface QrCodeDialogData {
  siteId: string;
  siteName: string;
  uuid: string;
}

const sanitizeFileNamePart = (value: string): string => value
  .replace(/[\\/:*?"<>|\x00-\x1F]/g, '_')
  .replace(/\s+/g, '_')
  .replace(/^\.+|\.+$/g, '')
  .trim();

@Component({
  selector: 'app-qr-code-dialog',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIconModule,
    QRCodeComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title>QR生成</h2>

    <mat-dialog-content>
      <div class="flex flex-col items-center gap-4 py-2">
        <p class="w-full text-center text-base font-medium break-words">{{ data.siteName }}</p>
        <qrcode
          [qrdata]="siteUrl()"
          [width]="240"
          [margin]="2"
          elementType="canvas"
          [ariaLabel]="data.siteName + ' QRコード'"
          [title]="data.siteName + ' QRコード'"
        />
        <div class="w-full rounded bg-primary-container p-3 text-sm break-all">
          {{ siteUrl() }}
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button matButton="tonal" type="button" (click)="copyUrl()">
        <mat-icon>{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
        URLコピー
      </button>
      <button matButton="tonal" type="button" (click)="downloadQrCode()">
        <mat-icon>download</mat-icon>
        ダウンロード
      </button>
      <button matButton type="button" (click)="close()">閉じる</button>
    </mat-dialog-actions>
  `
})
export class QrCodeDialogComponent {
  public readonly data = inject<QrCodeDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<QrCodeDialogComponent>);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly copied = signal(false);
  public readonly siteUrl = computed(() => `${globalThis.location.origin}/q/${this.data.uuid}`);
  private copyFeedbackTimeoutId: ReturnType<typeof setTimeout> | undefined;

  async copyUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.siteUrl());
      this.copied.set(true);

      if (this.copyFeedbackTimeoutId) {
        clearTimeout(this.copyFeedbackTimeoutId);
      }

      this.copyFeedbackTimeoutId = setTimeout(() => {
        this.copied.set(false);
        this.copyFeedbackTimeoutId = undefined;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy site URL:', error);
    }
  }

  downloadQrCode(): void {
    const canvas = this.elementRef.nativeElement.querySelector<HTMLCanvasElement>('qrcode canvas');
    if (!canvas) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = canvas.toDataURL('image/png');
    anchor.download = `qrcode_${this.downloadFileNamePart()}.png`;
    anchor.click();
  }

  close(): void {
    this.dialogRef.close();
  }

  private downloadFileNamePart(): string {
    return sanitizeFileNamePart(this.data.siteName) || sanitizeFileNamePart(this.data.siteId) || 'site';
  }
}
